<template>
  <section class="page-section">
    <div class="toolbar filter-wrap">
      <el-select v-model="filters.applyStatus" placeholder="补货状态" clearable class="toolbar-select">
        <el-option label="待补货" :value="0" />
        <el-option label="已入库" :value="1" />
        <el-option label="已拒绝" :value="2" />
      </el-select>
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="handleReset">重置</el-button>
    </div>

    <el-alert
      title="待补货单可确认入库或拒绝处理，入库成功后将自动增加库存。"
      type="info"
      :closable="false"
      show-icon
      class="section-alert"
    />

    <el-table :data="replenishments.content" class="business-table">
      <el-table-column prop="suppliesName" label="用品" min-width="140" />
      <el-table-column prop="suppliesSpec" label="规格" min-width="140" />
      <el-table-column prop="suppliesStock" label="库存" width="90" />
      <el-table-column label="单价" width="100">
        <template #default="{ row }">¥ {{ row.suppliesPrice }}</template>
      </el-table-column>
      <el-table-column prop="suppliesLocation" label="存放位置" min-width="140" show-overflow-tooltip />
      <el-table-column label="补货数量" width="100">
        <template #default="{ row }">
          {{ row.quantity ?? '需入库填写' }}
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="180" />
      <el-table-column label="审批时间" width="180">
        <template #default="{ row }">
          {{ row.applyTime || '未审批' }}
        </template>
      </el-table-column>
      <el-table-column label="补货状态" width="100">
        <template #default="{ row }">
          <el-tag :type="replenishmentTagType(row.applyStatus)">
            {{ row.statusLabel }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rejectReason" label="审批信息" min-width="180" show-overflow-tooltip />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <template v-if="row.applyStatus === 0">
            <el-button link type="primary" @click="openInboundDialog(row)">确认入库</el-button>
            <el-button link type="danger" @click="openReplenishmentRejectDialog(row)">拒绝</el-button>
          </template>
          <span v-else class="muted-text">已处理</span>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="replenishments.totalElements > 0" class="pager">
      <span class="pager-text">第 {{ filters.page }} / {{ totalPages }} 页</span>
      <el-button :disabled="filters.page <= 1" @click="handlePrevPage">
        <el-icon><ArrowLeft /></el-icon>
        上一页
      </el-button>
      <el-button type="primary" :disabled="filters.page >= totalPages" @click="handleNextPage">
        下一页
        <el-icon><ArrowRight /></el-icon>
      </el-button>
    </div>

    <el-dialog v-model="inboundVisible" title="确认入库" width="460px">
      <el-form label-position="top">
        <el-form-item label="用品名称">
          <el-input :model-value="currentReplenishment?.suppliesName || ''" disabled />
        </el-form-item>
        <el-form-item label="入库数量" required>
          <el-input-number v-model="inboundQuantity" class="full-width" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="inboundVisible = false">取消</el-button>
        <el-button type="primary" @click="submitInbound">确认入库</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="rejectVisible" title="填写审批信息" width="460px">
      <el-form label-position="top">
        <el-form-item label="审批信息" required>
          <el-input v-model="rejectReason" type="textarea" :rows="4" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" @click="submitReplenishmentReject">确认拒绝</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { ElMessage } from 'element-plus'
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import {
  approveAdminReplenishment,
  fetchAdminReplenishments,
  rejectAdminReplenishment
} from '../../services/adminService'

export default {
  name: 'AdminReplenishmentPanel',
  components: {
    ArrowLeft,
    ArrowRight
  },
  emits: ['notifications-changed'],
  data() {
    return {
      replenishments: {
        content: [],
        totalElements: 0
      },
      filters: {
        applyStatus: '',
        page: 1,
        pageSize: 10
      },
      inboundVisible: false,
      inboundQuantity: 1,
      rejectVisible: false,
      rejectReason: '',
      currentReplenishment: null
    }
  },
  computed: {
    totalPages() {
      const total = Number(this.replenishments.totalElements) || 0
      const pageSize = Number(this.filters.pageSize) || 10

      return Math.max(1, Math.ceil(total / pageSize))
    }
  },
  mounted() {
    this.loadReplenishments()
  },
  methods: {
    replenishmentTagType(status) {
      if (status === 0) return 'warning'
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'info'
    },
    async loadReplenishments() {
      const response = await fetchAdminReplenishments(this.filters)
      this.replenishments = response.data
    },
    handlePageChange(page) {
      this.filters.page = page
      this.loadReplenishments()
    },
    handlePrevPage() {
      if (this.filters.page <= 1) {
        return
      }

      this.handlePageChange(this.filters.page - 1)
    },
    handleNextPage() {
      if (this.filters.page >= this.totalPages) {
        return
      }

      this.handlePageChange(this.filters.page + 1)
    },
    handleSearch() {
      this.filters.page = 1
      this.loadReplenishments()
    },
    handleReset() {
      this.filters.applyStatus = ''
      this.filters.page = 1
      this.loadReplenishments()
    },
    openInboundDialog(row) {
      this.currentReplenishment = row
      this.inboundQuantity = row.quantity || 1
      this.inboundVisible = true
    },
    openReplenishmentRejectDialog(row) {
      this.currentReplenishment = row
      this.rejectReason = ''
      this.rejectVisible = true
    },
    async submitInbound() {
      const inboundQuantity = Number(this.inboundQuantity)

      if (!Number.isFinite(inboundQuantity) || inboundQuantity === 0) {
        ElMessage.warning('入库数量不能为 0')
        return
      }

      try {
        const response = await approveAdminReplenishment(this.currentReplenishment.id, inboundQuantity)
        ElMessage.success(response.message || response.msg || '补货单处理成功')
        this.inboundVisible = false
        this.loadReplenishments()
        this.$emit('notifications-changed')
      } catch (error) {
        ElMessage.error(error.message)
      }
    },
    async submitReplenishmentReject() {
      const rejectReason = this.rejectReason.trim()

      if (!rejectReason) {
        ElMessage.warning('请填写审批信息')
        return
      }

      try {
        const response = await rejectAdminReplenishment(this.currentReplenishment.id, rejectReason)
        ElMessage.success(response.message || response.msg || '补货单处理成功')
        this.rejectVisible = false
        this.loadReplenishments()
        this.$emit('notifications-changed')
      } catch (error) {
        ElMessage.error(error.message)
      }
    }
  }
}
</script>

<style scoped>
.page-section {
  display: grid;
  gap: 22px;
}

.business-table {
  border-radius: 22px;
  overflow: hidden;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-wrap {
  justify-content: flex-start;
}

.toolbar-select {
  width: 180px;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pager-text {
  color: #475569;
  font-size: 14px;
  font-weight: 600;
}

.section-alert {
  border-radius: 16px;
}

.muted-text {
  color: #94a3b8;
}

.full-width {
  width: 100%;
}

@media (max-width: 768px) {
  .toolbar-select {
    width: 100%;
  }
}
</style>
